import { useMutation, useQueryClient } from 'react-query';
import { authFetch } from '../../../../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../../../../CommonUtils/httpBodyOptions';
import { FinancialPlanDTO } from '../../../../types/financialPlan.types';
import { queryKeys } from '../../../../api/queryKeys';
import { queryKeys as actionStepsReportQueryKeys } from '../../../../../ActionStepsReport/api/queryKeys';
import { ActionStepsDTO } from '../../../../../ActionStepsReport/api/useActionStepsQuery';

interface UpdateActionStepStateParams {
  GoalId: string;
  ActionStepKey: string;
  clientId: number;
  fpId: string;
  Completion: boolean;
  Text: string;
  isReport?: boolean;
}

const updateActionStepState = async ({
  GoalId,
  ActionStepKey,
  clientId,
  fpId,
  Text,
  Completion,
}: UpdateActionStepStateParams): Promise<null> =>
  authFetch('/Consumer/UpdatePlanActionSteps/UpdateTaskState', {
    method: 'POST',
    ...httpBodyOptions(
      {
        ActionStepKey,
        Completion,
        GoalId,
        Text,
        clientId,
        fpId,
      },
      true,
    ),
  });

export const useUpdateActionStepStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    null,
    Error,
    UpdateActionStepStateParams,
    { previousState: FinancialPlanDTO | ActionStepsDTO | undefined }
  >(updateActionStepState, {
    onMutate: async ({ ActionStepKey, Completion, clientId, fpId, isReport }) => {
      if (isReport) {
        const queryKey = actionStepsReportQueryKeys.actionSteps(clientId, fpId);

        const previousState = queryClient.getQueryData<ActionStepsDTO | undefined>(queryKey);

        queryClient.setQueryData<ActionStepsDTO | undefined>(queryKey, old => {
          if (old) {
            return {
              ...old,
              Tasks: old.Tasks.map(actionStep => {
                if (actionStep.ActionStepKey === ActionStepKey) {
                  return {
                    ...actionStep,
                    IsComplete: Completion,
                  };
                }

                return actionStep;
              }),
            };
          }

          return old;
        });

        return { previousState };
      }

      const queryKey = queryKeys.financialPlan(clientId, fpId);

      const previousState = queryClient.getQueryData<FinancialPlanDTO | undefined>(queryKey);

      queryClient.setQueryData<FinancialPlanDTO | undefined>(queryKey, old => {
        if (old && old.PlanActionSteps) {
          return {
            ...old,
            PlanActionSteps: old.PlanActionSteps.map(actionStep => {
              if (actionStep.ActionStepKey === ActionStepKey) {
                return {
                  ...actionStep,
                  IsComplete: Completion,
                };
              }

              return actionStep;
            }),
          };
        }

        return old;
      });

      return { previousState };
    },

    onError: (err, { clientId, fpId, isReport }, context) => {
      if (context && context.previousState) {
        const queryKey = isReport
          ? actionStepsReportQueryKeys.actionSteps(clientId, fpId)
          : queryKeys.financialPlan(clientId, fpId);

        queryClient.setQueryData(queryKey, context.previousState);
      }
    },
  });
};
